/**
 * StratIQX Navigation Configuration
 * Single source of truth for all main navigation across the application
 */

export interface NavigationItem {
  id: string
  label: string
  path: string
  description?: string
  external?: boolean
  showInFooter?: boolean
  displayOrder?: number
}

/**
 * Main navigation items for the application
 * These appear in the primary navigation header
 */
export const MAIN_NAVIGATION: NavigationItem[] = [
  {
    id: 'solutions',
    label: 'Solutions',
    path: '/intelligence-solutions',
    description: 'Our AI-powered intelligence services',
    showInFooter: true,
    displayOrder: 1
  },
  {
    id: 'use-cases',
    label: 'Use Cases',
    path: '/use-cases',
    description: 'See how clients leverage our platform',
    showInFooter: true,
    displayOrder: 2
  }
  // Future navigation items can be easily added here:
  // {
  //   id: 'about',
  //   label: 'About',
  //   path: '/about',
  //   description: 'Learn about StratIQX and our mission',
  //   showInFooter: true,
  //   displayOrder: 3
  // },
  // {
  //   id: 'contact',
  //   label: 'Contact Us',
  //   path: '/contact',
  //   description: 'Get in touch with our team',
  //   showInFooter: true,
  //   displayOrder: 4
  // },
  // {
  //   id: 'pricing',
  //   label: 'Pricing',
  //   path: '/pricing',
  //   description: 'View our service pricing and plans',
  //   showInFooter: false,
  //   displayOrder: 5
  // }
]

/**
 * Footer navigation items (legal pages, etc.)
 */
export const FOOTER_NAVIGATION: NavigationItem[] = [
  {
    id: 'privacy',
    label: 'Privacy Policy',
    path: '/privacy',
    description: 'Our privacy policy and data handling practices',
    displayOrder: 1
  },
  {
    id: 'terms',
    label: 'Terms of Service',
    path: '/terms',
    description: 'Terms and conditions for using StratIQX',
    displayOrder: 2
  },
  {
    id: 'cookies',
    label: 'Cookie Policy',
    path: '/cookies',
    description: 'Information about our cookie usage',
    displayOrder: 3
  }
]

/**
 * Special action items (CTAs, buttons)
 */
export const NAVIGATION_ACTIONS = {
  primary: {
    label: 'Get Intelligence Report',
    path: '/onboarding-info',
    description: 'Start your strategic intelligence journey'
  },
  secondary: {
    label: 'View Dashboard',
    path: '/admin/dashboard',
    description: 'Access your StratIQX dashboard'
  }
} as const

/**
 * Navigation utilities
 */
export class NavigationConfig {
  /**
   * Get all main navigation items sorted by display order
   */
  static getMainNavigation(): NavigationItem[] {
    return MAIN_NAVIGATION.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
  }

  /**
   * Get footer navigation items sorted by display order
   */
  static getFooterNavigation(): NavigationItem[] {
    return FOOTER_NAVIGATION.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
  }

  /**
   * Get navigation items that should show in footer
   */
  static getFooterVisibleNavigation(): NavigationItem[] {
    const mainInFooter = MAIN_NAVIGATION.filter(item => item.showInFooter)
    return [...mainInFooter, ...FOOTER_NAVIGATION]
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
  }

  /**
   * Find navigation item by ID
   */
  static findNavigationItem(id: string): NavigationItem | undefined {
    return [...MAIN_NAVIGATION, ...FOOTER_NAVIGATION].find(item => item.id === id)
  }

  /**
   * Find navigation item by path
   */
  static findNavigationByPath(path: string): NavigationItem | undefined {
    return [...MAIN_NAVIGATION, ...FOOTER_NAVIGATION].find(item => item.path === path)
  }

  /**
   * Check if a path is in main navigation
   */
  static isMainNavigationPath(path: string): boolean {
    return MAIN_NAVIGATION.some(item => item.path === path)
  }

  /**
   * Get navigation breadcrumb trail for a path
   */
  static getBreadcrumb(path: string): NavigationItem[] {
    const item = this.findNavigationByPath(path)
    if (!item) return []
    
    // For now, simple single-level breadcrumb
    // Can be extended for nested navigation in the future
    return [
      { id: 'home', label: 'Home', path: '/' },
      item
    ]
  }

  /**
   * Validate navigation configuration
   */
  static validateNavigation(): { isValid: boolean; errors: string[] } {
    const errors: string[] = []
    const allItems = [...MAIN_NAVIGATION, ...FOOTER_NAVIGATION]
    
    // Check for duplicate IDs
    const ids = allItems.map(item => item.id)
    const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index)
    if (duplicateIds.length > 0) {
      errors.push(`Duplicate navigation IDs: ${duplicateIds.join(', ')}`)
    }
    
    // Check for duplicate paths
    const paths = allItems.map(item => item.path)
    const duplicatePaths = paths.filter((path, index) => paths.indexOf(path) !== index)
    if (duplicatePaths.length > 0) {
      errors.push(`Duplicate navigation paths: ${duplicatePaths.join(', ')}`)
    }
    
    // Check for missing required fields
    allItems.forEach(item => {
      if (!item.id) errors.push('Navigation item missing ID')
      if (!item.label) errors.push(`Navigation item ${item.id} missing label`)
      if (!item.path) errors.push(`Navigation item ${item.id} missing path`)
    })
    
    return {
      isValid: errors.length === 0,
      errors
    }
  }
}

/**
 * Footer-specific configuration interfaces
 */
export interface FooterSection {
  id: string
  title: string
  items: FooterItem[]
  displayOrder?: number
}

export interface FooterItem {
  id: string
  label: string
  path?: string
  external?: boolean
  description?: string
  icon?: string
}

export interface CompanyInfo {
  name: string
  tagline: string
  description: string
  copyright: string
  contactEmail: string
  contactPhone?: string
  availability: string
}

/**
 * Company information configuration
 */
export const COMPANY_INFO: CompanyInfo = {
  name: 'StratIQX',
  tagline: 'Strategic Intelligence Amplified',
  description: 'Transforming complex market data into clear strategic advantage through AI-powered research and human expertise.',
  copyright: '© 2025 StratIQX. Strategic Intelligence Amplified through AI orchestration and human expertise.',
  contactEmail: 'intelligence@stratiqx.ai',
  availability: 'Available globally'
}

/**
 * Footer sections configuration
 */
export const FOOTER_SECTIONS: FooterSection[] = [
  {
    id: 'services',
    title: 'Intelligence Services',
    displayOrder: 1,
    items: [
      // These will be dynamically populated from active services
      // Static fallback items can be defined here if needed
    ]
  },
  {
    id: 'expertise',
    title: 'Industry Expertise',
    displayOrder: 2,
    items: [
      { id: 'technology', label: 'Technology & SaaS', description: 'Software and technology companies' },
      { id: 'healthcare', label: 'Healthcare & Biotech', description: 'Medical and biotechnology sectors' },
      { id: 'financial', label: 'Financial Services', description: 'Banking and financial institutions' },
      { id: 'manufacturing', label: 'Manufacturing & Industrial', description: 'Industrial and manufacturing' },
      { id: 'automotive', label: 'Automotive & Transportation', description: 'Transportation industry' },
      { id: 'professional', label: 'Professional Services', description: 'Service-based businesses' }
    ]
  },
  {
    id: 'contact',
    title: 'Get Started',
    displayOrder: 3,
    items: [
      { 
        id: 'email', 
        label: `📧 ${COMPANY_INFO.contactEmail}`,
        description: 'Email us for inquiries'
      },
      ...(COMPANY_INFO.contactPhone ? [{ 
        id: 'phone', 
        label: `📱 ${COMPANY_INFO.contactPhone}`,
        description: 'Call our intelligence team'
      }] : []),
      { 
        id: 'availability', 
        label: `🌐 ${COMPANY_INFO.availability}`,
        description: 'We serve clients worldwide'
      }
    ]
  }
]

/**
 * Footer configuration utilities
 */
export class FooterConfig {
  /**
   * Get all footer sections sorted by display order
   */
  static getFooterSections(): FooterSection[] {
    return FOOTER_SECTIONS.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
  }

  /**
   * Get footer section by ID
   */
  static getFooterSection(sectionId: string): FooterSection | undefined {
    return FOOTER_SECTIONS.find(section => section.id === sectionId)
  }

  /**
   * Get services footer section with dynamic content
   */
  static getServicesFooterSection(): FooterSection {
    const servicesSection = this.getFooterSection('services')
    if (!servicesSection) {
      return {
        id: 'services',
        title: 'Intelligence Services',
        items: []
      }
    }

    // Return section - actual service items will be populated by the footer component
    return servicesSection
  }

  /**
   * Get company information
   */
  static getCompanyInfo(): CompanyInfo {
    return COMPANY_INFO
  }

  /**
   * Get footer legal links (from existing navigation config)
   */
  static getFooterLegalLinks(): NavigationItem[] {
    return NavigationConfig.getFooterNavigation()
  }

  /**
   * Generate complete footer configuration for a specific variant
   */
  static getFooterConfig(variant: 'full' | 'minimal' | 'legal') {
    const companyInfo = this.getCompanyInfo()
    const legalLinks = this.getFooterLegalLinks()
    
    switch (variant) {
      case 'full':
        return {
          companyInfo,
          sections: this.getFooterSections(),
          legalLinks,
          showSections: true,
          showContact: true,
          showDescription: true
        }
      
      case 'minimal':
        return {
          companyInfo,
          sections: [],
          legalLinks,
          showSections: false,
          showContact: false,
          showDescription: false
        }
      
      case 'legal':
        return {
          companyInfo,
          sections: [],
          legalLinks,
          showSections: false,
          showContact: false,
          showDescription: false
        }
      
      default:
        return this.getFooterConfig('minimal')
    }
  }

  /**
   * Validate footer configuration
   */
  static validateFooterConfig(): { isValid: boolean; errors: string[] } {
    const errors: string[] = []
    
    // Validate company info
    const companyInfo = this.getCompanyInfo()
    if (!companyInfo.name) errors.push('Company name is required')
    if (!companyInfo.contactEmail) errors.push('Contact email is required')
    
    // Validate footer sections
    const sections = this.getFooterSections()
    const sectionIds = sections.map(s => s.id)
    const duplicateIds = sectionIds.filter((id, index) => sectionIds.indexOf(id) !== index)
    if (duplicateIds.length > 0) {
      errors.push(`Duplicate footer section IDs: ${duplicateIds.join(', ')}`)
    }
    
    // Validate section items
    sections.forEach(section => {
      if (!section.title) {
        errors.push(`Footer section ${section.id} missing title`)
      }
      section.items.forEach(item => {
        if (!item.label) {
          errors.push(`Footer item in section ${section.id} missing label`)
        }
      })
    })
    
    return {
      isValid: errors.length === 0,
      errors
    }
  }
}

/**
 * Export commonly used functions for direct import
 */
export const {
  getMainNavigation,
  getFooterNavigation,
  getFooterVisibleNavigation,
  findNavigationItem,
  findNavigationByPath,
  isMainNavigationPath,
  getBreadcrumb
} = NavigationConfig

export const {
  getFooterSections,
  getFooterSection,
  getServicesFooterSection,
  getCompanyInfo,
  getFooterLegalLinks,
  getFooterConfig
} = FooterConfig